import type { ResultOf } from '@graphql-typed-document-node/core'
import { NoImage } from 'macaron-frontend-ui'
import Image from '~/components/image/Image'
import type { PlanSeat } from '~/features/plan/planSeats'
import { usePlanSeats } from '~/features/plan/planSeats'
import type { DateText, HourMinute, PlanId, RestaurantId } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { seatLabel } from '~/lib/seat/seat2'
import { useIsPC } from '~/lib/ua'
import { sva } from '~/styled-system/css'

type Props = {
  restaurantId: RestaurantId
  planId: PlanId
  visitorsCount?: number
  visitDate?: DateText
  visitTime?: HourMinute
}

const styles = sva({
  slots: ['root', 'container', 'content', 'text', 'title', 'description', 'notis'],
  base: {
    root: {
      spaceY: '2',
      _pc: {
        spaceY: '3',
      },
    },
    container: {
      bgColor: 'white',
      p: '3',
      rounded: 'md',
      _pc: {
        border: 'medium',
      },
    },
    content: {
      display: 'flex',
      spaceX: '2',
      _pc: {
        spaceX: '3',
      },
    },
    text: {
      flex: '1',
      spaceY: '1',
      _pc: {
        spaceY: '2',
      },
    },
    title: {
      fontSize: 'base',
      fontWeight: 'bold',
      _pc: {
        fontSize: 'md',
      },
    },
    description: {
      fontSize: 'sm',
      color: 'gray.700',
    },
    notis: {
      pb: '2',
      pt: '4',
      textAlign: 'right',
      fontSize: 'xs',
      color: 'gray.600',
    },
  },
})

export function SeatList({ restaurantId, planId, visitorsCount, visitDate, visitTime }: Props) {
  const seats = usePlanSeats({ restaurantId, planId, visitorsCount, visitDate, visitTime })
  const isPC = useIsPC()
  const classes = styles()

  if (seats.length === 0) {
    return null
  }

  return (
    <>
      {!isPC && <div className={classes.notis}>※写真はイメージです</div>}
      <div className={classes.root}>
        {seats.map((s) => (
          <Seat seat={s} key={s.seatId} />
        ))}
      </div>
    </>
  )
}

function Seat({ seat }: { seat: ResultOf<typeof PlanSeat> }) {
  const label = seatLabel(seat)
  const classes = styles()

  return (
    <section className={classes.container}>
      <div className={classes.content}>
        <SeatImage image={seat.image} label={label} />
        <div className={classes.text}>
          <p className={classes.title}>{label}</p>
          <Description seat={seat} />
        </div>
      </div>
    </section>
  )
}

function SeatImage({ image, label }: { image: ResultOf<typeof PlanSeat>['image']; label: string }) {
  const isPC = useIsPC()
  const imageWidth = isPC ? 90 : 70
  const imageHeight = isPC ? 90 : 70
  if (image === null) {
    return <NoImage width={imageWidth} height={imageHeight} />
  }
  return (
    <Image
      alt={label}
      src={image.url}
      width={imageWidth}
      height={imageHeight}
      className="rounded"
    />
  )
}

function Description({ seat }: { seat: ResultOf<typeof PlanSeat> }) {
  return (
    <div className="text-sm leading-snug text-neutral-700">
      {seat.charge.__typename !== 'SeatChargeNoCharge' && <p>チャージ料 {chargeText(seat)}</p>}
      {seat.seatTypeDescription !== null && <p>{seat.seatTypeDescription}</p>}
    </div>
  )
}

function chargeText(seat: ResultOf<typeof PlanSeat>) {
  // 1名(1卓)あたりのチャージ料を表示する
  return seat.charge.__typename === 'SeatChargePerPerson'
    ? `${money(seat.charge.price)}円／名`
    : seat.charge.__typename === 'SeatChargePerTable'
      ? `${money(seat.charge.price)}円／卓`
      : ''
}
